
import { useMsal } from '@azure/msal-react';
import {
    makeStyles,
    Select,
    Spinner,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { ChevronDown24Regular, ChevronRight24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ProjektItem } from '../../models/ProjektItem';
import type { ProjektKostenItem } from '../../models/ProjektKostenItem';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    filterSection: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        alignItems: 'flex-end',
        marginBottom: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    filterLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
    },
    selectWidth: {
        minWidth: '200px',
    },
    groupHeader: {
        fontWeight: 400,
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
    },
    tableHeader: {
        fontWeight: 400,
        backgroundColor: tokens.colorNeutralBackground2,
        color: tokens.colorNeutralForeground1,
        borderBottom: `2px solid ${tokens.colorNeutralStroke1}`,
    },
    groupRow: {
        paddingLeft: tokens.spacingHorizontalL,
    },
    currencyCell: {
        textAlign: 'right' as const,
    },
    stickyTableWrapper: {
        overflow: 'auto',
        maxHeight: '100%',
    },
    stickyFirstCell: {
        position: 'sticky' as const,
        left: 0,
        backgroundColor: 'inherit',
        zIndex: 1,
        minWidth: '150px',
    },
});

interface ProjektKostenProps {
    project: ProjektItem | null;
}

const formatCurrency = (value: number | undefined): string => {
    if (value === undefined || value === null) return '€ 0.00';
    return `€ ${value.toFixed(2).replace('.', ',')}`;
};

const getSchemaColumns = (schema: number): string[] => {
    if (schema === 2) { // 5000 (IMCProjects)
        return [
            'Personnel Costs (Time Recording)',
            'Direct Personnel Costs',
            'External Personnel Costs/Third-party Services',
            'Travel Costs',
            'Material Costs',
            'Research Materials',
            'Advertising',
            'IT Expenses, Fixed Assets',
            'Rent and Operating Costs',
            'Other Material Costs',
            'Depreciation',
            'Operating Performance',
        ];
    }
    if (schema === 1) { // 4005 (BIS)
        return [
            'Personnel Costs (Time Recording)',
            'Personnel Costs (Payroll)',
            'Other Internal Costs',
            'External Costs',
        ];
    }
    // Default
    return [];
};

const formatPeriode = (jahrMonatAsText: string | null | undefined): string => {
    if (!jahrMonatAsText) return '-';
    // If it's a 4-digit string starting with "00", convert to full year (e.g., "0016" -> "2016")
    if (/^\d{4}$/.test(jahrMonatAsText) && jahrMonatAsText.startsWith('00')) {
        const year = parseInt(jahrMonatAsText, 10);
        return (2000 + year).toString();
    }
    return jahrMonatAsText;
};

const ProjektKosten: React.FC<ProjektKostenProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [kostenData, setKostenData] = useState<ProjektKostenItem[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [schemaId, setSchemaId] = useState<number>(2);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    const toggleGroup = useCallback((year: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(year)) {
                newSet.delete(year);
            } else {
                newSet.add(year);
            }
            return newSet;
        });
    }, []);

    // Fetch kosten data
    const fetchKostenData = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);
            const data = await dataService.getProjektkostenListe(project.projektID, schemaId);
            console.log('Fetched kosten data:', data);
            setKostenData(data || []);
        } catch (err) {
            console.error('Error loading kosten data:', err);
            setError('Error loading cost data');
            setKostenData([]);
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService, schemaId]);

    useEffect(() => {
        fetchKostenData();
    }, [schemaId, fetchKostenData]);

    // Initialize expanded groups when data loads
    useEffect(() => {
        if (kostenData.length > 0) {
            const years = new Set<string>();
            kostenData.forEach(item => {
                const year = item.wirtschaftsjahr || 'Unbekannt';
                years.add(year);
            });
            setExpandedGroups(years);
        }
    }, [kostenData]);

    // Group data by fiscal year
    const groupedData = useMemo(() => {
        const groups: { [key: string]: ProjektKostenItem[] } = {};

        kostenData.forEach(item => {
            const year = item.wirtschaftsjahr || 'Unbekannt';
            if (!groups[year]) {
                groups[year] = [];
            }
            groups[year].push(item);
        });

        return { groups };
    }, [kostenData]);

    const sortedKostenData = useMemo(
        () => [...kostenData].sort((a, b) => (a.wirtschaftsjahr || '').localeCompare(b.wirtschaftsjahr || '')),
        [kostenData],
    );

    const excelColumns = useMemo<ExcelExportColumn<ProjektKostenItem>[]>(() => [
        { header: 'Fiscal Year', value: (item) => item.wirtschaftsjahr || 'Unbekannt', width: 14 },
        { header: 'Period', value: (item) => formatPeriode(item.jahrMonatAsText), width: 14 },
        ...getSchemaColumns(schemaId).map((columnName, colIndex): ExcelExportColumn<ProjektKostenItem> => ({
            header: columnName,
            // Same mapping as the table: first column from time recording, others from kostenPositionen
            value: (item) => colIndex === 0
                ? item.personalkostenZeitaufzeichnung || 0
                : item.kostenPositionen?.[colIndex] || 0,
            width: 22,
        })),
    ], [schemaId]);

    if (!project) return null;

    const renderTableContent = () => {
        const rows: React.ReactNode[] = [];
        const { groups } = groupedData;
        const sortedYears = Object.keys(groups).sort();
        const columns = getSchemaColumns(schemaId);
        const totalColumns = 1 + columns.length; // Zeitraum + number of schema columns

        // Render groups
        sortedYears.forEach(year => {
            const isExpanded = expandedGroups.has(year);

            // Group header with chevron - spans full width
            rows.push(
                <TableRow
                    key={`header-${year}`}
                    onClick={() => toggleGroup(year)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                    <TableCell colSpan={totalColumns}>
                        <TableCellLayout className={styles.groupHeader} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {isExpanded ? <ChevronDown24Regular /> : <ChevronRight24Regular />}
                            Fiscal Year {year}
                        </TableCellLayout>
                    </TableCell>
                </TableRow>
            );

            // Detail rows for this year (only if expanded)
            if (isExpanded) {
                groups[year].forEach((item: ProjektKostenItem, itemIndex: number) => {
                    rows.push(
                        <TableRow key={`${year}-${itemIndex}`}>
                            <TableCell>
                                <TableCellLayout className={styles.groupRow}>
                                    {formatPeriode(item.jahrMonatAsText)}
                                </TableCellLayout>
                            </TableCell>
                            {columns.map((_columnName: string, colIndex: number) => {
                                // First column (colIndex 0) uses personalkostenZeitaufzeichnung
                                let value = 0;
                                if (colIndex === 0) {
                                    value = item.personalkostenZeitaufzeichnung || 0;
                                } else {
                                    // Other columns get from kostenPositionen
                                    value = item.kostenPositionen?.[colIndex] || 0;
                                }
                                return (
                                    <TableCell key={`${year}-${itemIndex}-col-${colIndex}`}>
                                        <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                            {formatCurrency(value)}
                                        </TableCellLayout>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    );
                });
            }
        });

        return rows;
    };

    return (
        <div className={styles.container}>
            <div className={styles.filterSection}>
                <div className={styles.filterGroup}>
                    <label className={styles.filterLabel}>Schema</label>
                    <Select
                        value={schemaId.toString()}
                        onChange={(_e, data) => {
                            setSchemaId(parseInt(data.value, 10));
                        }}
                        className={styles.selectWidth}
                    >
                        <option value="1">4005 (BIS)</option>
                        <option value="2">5000 (IMCProjects)</option>
                    </Select>
                </div>
                <ExcelExportButton
                    items={isLoading ? [] : sortedKostenData}
                    columns={excelColumns}
                    fileName={`ProjectCosts_${project.projektID}_${new Date().toISOString().split('T')[0]}`}
                    sheetName="Project Costs"
                />
            </div>

            {error && (
                <div className={styles.emptyState} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {error}
                </div>
            )}

            {!error && kostenData.length === 0 && !isLoading && (
                <div className={styles.emptyState}>
                    No cost data available
                </div>
            )}

            {!isLoading && kostenData.length > 0 ? (
                <div className={styles.tableContainer} style={{ overflowX: 'auto' }}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow style={{ backgroundColor: tokens.colorNeutralBackground2, borderBottom: `2px solid ${tokens.colorNeutralStroke1}` }}>
                                <TableHeaderCell>
                                    <div style={{ fontWeight: 700 }}>Period</div>
                                </TableHeaderCell>
                                {getSchemaColumns(schemaId).map(columnName => (
                                    <TableHeaderCell key={columnName}>
                                        <div style={{ textAlign: 'right', display: 'block', width: '100%', fontWeight: 700 }}>
                                            {columnName}
                                        </div>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {renderTableContent()}
                        </TableBody>
                    </Table>
                </div>
            ) : isLoading ? (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading cost data..." />
                </div>
            ) : null}
        </div>
    );
};

export default ProjektKosten;
